/**
 * WagePro-branded login screen (WagePro signup epic — added per direct user
 * request after manual testing surfaced the gap: `SignedOutScreen`'s "Sign
 * In" button used to jump straight to Ledger's generic, un-WagePro-branded
 * `/login`, which was the only login destination that existed. Only signup
 * had a branded screen before this).
 *
 * Sibling of `wagepro_signup_screen.tsx` — same visual structure, same
 * cookie-based `getTrpcClient()` call pattern (WagePro standalone-admin-auth
 * pivot off OAuth — see `apps/api/src/trpc/routers/wagepro_auth.ts`'s module
 * docblock). Calls `wageproAuth.login`, then simply navigates to `/` — the
 * session cookie `wageproAuth.login` sets IS the login now, no OAuth hop
 * needed after it. When the account has two-step verification on, the
 * server answers `two_factor_required` (no session yet) and this screen
 * swaps its form for `TwoFactorCodeStep`; when the company requires
 * two-step verification and the account has not set it up, the server
 * answers `two_factor_setup_required` and the screen shows
 * `TwoFactorSetupStep`. Every path finishes through `finish_sign_in.ts`.
 *
 * Rendered by `main.tsx` BEFORE `<AuthProvider><AuthGate/></AuthProvider>`
 * mounts, gated on `window.location.pathname === LOGIN_PATH` — same
 * pre-auth pathname-gate mechanism as `WageProSignupScreen`/
 * `InviteAcceptScreen`.
 *
 * Also the landing target of `AuthGate`'s cold-boot/sign-out auto-redirect
 * (`main.tsx`) — `wasForceReauthRequested` reads the
 * `?reauth=1` query param that redirect carries for a deliberate sign-out,
 * purely to show a "you were signed out" message.
 *
 * Wrapped in `WageProAuthLayout` (split-pane brand chrome matching Ledger's
 * own login page archetype, restyle pass added per direct user request) —
 * this component supplies only the form content, not the page shell.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  FormField,
  GlassCard,
  Input,
  PasswordInput,
} from '@jumpstone/ui-react';
import { getTrpcClient } from '../../lib/trpc.js';
import { isNative } from '../../lib/platform.js';
import { nativeClientInput } from '../../lib/auth/native_shell_auth.js';
import { SIGNUP_PATH } from '../../lib/auth/wagepro_signup_logic.js';
import { INVITE_CODE_PATH } from '../../lib/auth/invite_accept_logic.js';
import { beginSignIn } from '../../lib/auth/oauth_pkce.js';
import { LedgerSsoHandoffScreen } from '../../components/auth/session_boot_states.js';
import { AuthAltActionLink } from './auth_alt_action_link.js';
import { AuthFormError } from './auth_form_error.js';
import { WageProAuthLayout } from '../../components/layout/wagepro_auth_layout.js';
import {
  classifyAuthError,
  type AuthErrorDisplay,
} from '../../lib/auth/auth_error_display.js';
import {
  hasLoginFieldErrors,
  readLedgerSsoEntityHint,
  validateLoginForm,
  wasForceReauthRequested,
  wasLedgerSsoHandoffRequested,
  type LoginFieldErrors,
  type WageProLoginFormValues,
} from '../../lib/auth/wagepro_login_logic.js';
import {
  nextLoginStep,
  type LoginNextStep,
} from '../../lib/auth/two_factor_logic.js';
import { finishSignIn } from './finish_sign_in.js';
import { TwoFactorCodeStep } from './two_factor_code_step.js';
import { TwoFactorSetupStep } from './two_factor_setup_step.js';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; error: AuthErrorDisplay };

const EMPTY_VALUES: WageProLoginFormValues = {
  email: '',
  password: '',
};

export function WageProLoginScreen({
  onSignedIn,
}: {
  /** Called right before navigating to `/` on a successful sign-in. */
  onSignedIn?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [values, setValues] = useState<WageProLoginFormValues>(EMPTY_VALUES);
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({});
  const [state, setState] = useState<SubmitState>({ kind: 'idle' });
  // The second-factor step, once the password has been accepted. `null`
  // while the email/password form is showing.
  const [twoFactorStep, setTwoFactorStep] = useState<Exclude<
    LoginNextStep,
    { kind: 'signed_in' }
  > | null>(null);
  // Captured once, lazily — this screen never remounts mid-flow (no router),
  // so a plain `useState` initializer is sufficient, same pattern as
  // `SIGNUP_PATH`/`INVITE_ACCEPT_PATH`'s own lazy pathname checks in
  // `main.tsx`. See `wagepro_login_logic.ts`'s `FORCE_REAUTH_PARAM` docblock.
  const [forcedReauth] = useState(() =>
    wasForceReauthRequested(window.location.search),
  );
  // Ledger Apps launcher auto-handoff (issue #23943). The launcher opens
  // WagePro at `/login?sso=ledger`; that param tells this screen to skip the
  // credentials form and go straight to `beginSignIn()` — the "Sign in with
  // Ledger" PKCE handoff that would otherwise run AFTER a successful
  // password submit, but for a user already signed in to Ledger there is
  // nothing to submit. The signed-out defense from PR #22341 is preserved:
  // `wasLedgerSsoHandoffRequested` returns `false` when `?reauth=1` is also
  // present (see `wagepro_login_logic.ts`'s `LEDGER_SSO_PARAM` docblock), so
  // a deliberate sign-out still lands on the form.
  const [ledgerSsoHandoff] = useState(() =>
    wasLedgerSsoHandoffRequested(window.location.search),
  );
  const [ledgerSsoEntityHint] = useState(() =>
    readLedgerSsoEntityHint(window.location.search),
  );
  const [ledgerSsoHandoffFailed, setLedgerSsoHandoffFailed] = useState(false);
  useEffect(() => {
    if (!ledgerSsoHandoff) return;
    // A rejected `beginSignIn()` (no `crypto.subtle`, blocked
    // `sessionStorage`) falls back to rendering the credentials form so the
    // user isn't stranded on a redirecting placeholder that never navigates.
    const entityId = ledgerSsoEntityHint ?? undefined;
    void beginSignIn({}, { entityId }).catch(() => {
      setLedgerSsoHandoffFailed(true);
    });
  }, [ledgerSsoHandoff, ledgerSsoEntityHint]);

  if (ledgerSsoHandoff && !ledgerSsoHandoffFailed) {
    return <LedgerSsoHandoffScreen />;
  }

  function set<K extends keyof WageProLoginFormValues>(
    key: K,
    value: WageProLoginFormValues[K],
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(): Promise<void> {
    const errors = validateLoginForm(values);
    setFieldErrors(errors);
    if (hasLoginFieldErrors(errors)) return;

    setState({ kind: 'submitting' });
    // ONLY the mutation is guarded — see the equivalent note in
    // `wagepro_signup_screen.tsx`: a `TypeError` thrown by `onSignedIn` after
    // a SUCCESSFUL login would otherwise be classified as a transport failure
    // and told to the user as "check your internet connection".
    let next: LoginNextStep;
    try {
      next = nextLoginStep(
        await getTrpcClient().wageproAuth.login.mutate({
          email: values.email.trim(),
          password: values.password,
          ...nativeClientInput(isNative()),
        }),
      );
    } catch (err) {
      setState({ kind: 'error', error: classifyAuthError(err) });
      return;
    }

    if (next.kind !== 'signed_in') {
      // Password accepted, second factor outstanding — no session yet. The
      // password is dropped from memory; the step carries only its token.
      setValues((prev) => ({ ...prev, password: '' }));
      setState({ kind: 'idle' });
      setTwoFactorStep(next);
      return;
    }

    // Deliberately OUTSIDE the try above, and NOT classified with
    // `classifyAuthError`: persisting the native session is local storage,
    // not the network — see `complete_sign_in.ts`.
    const outcome = await finishSignIn(next, onSignedIn);
    if (outcome === 'session_not_stored') {
      // Either the store rejected, or the API answered without a token (an
      // API older than this shell). Both mean we cannot authenticate the
      // next request, so show an error rather than boot into a 401 loop.
      setState({ kind: 'error', error: { kind: 'unknown' } });
    }
  }

  function startOver(): void {
    setTwoFactorStep(null);
    setState({ kind: 'idle' });
  }

  const submitting = state.kind === 'submitting';

  if (twoFactorStep !== null) {
    return (
      <WageProAuthLayout>
        <GlassCard variant="strong" className="rounded-2xl p-8 w-full">
          {twoFactorStep.kind === 'enter_code' ? (
            <TwoFactorCodeStep
              pendingToken={twoFactorStep.pending_token}
              onSignedIn={onSignedIn}
              onStartOver={startOver}
            />
          ) : (
            <TwoFactorSetupStep
              setupToken={twoFactorStep.setup_token}
              bindingSecret={twoFactorStep.binding_secret}
              onSignedIn={onSignedIn}
              onStartOver={startOver}
            />
          )}
        </GlassCard>
      </WageProAuthLayout>
    );
  }

  return (
    <WageProAuthLayout>
      <GlassCard variant="strong" className="rounded-2xl p-8 w-full">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-[color:var(--ink)]">
            {t('login.title')}
          </h1>
          <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
            {forcedReauth ? t('login.signed_out_dek') : t('login.dek')}
          </p>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <FormField
            label={t('login.email_label')}
            htmlFor="login-email"
            error={fieldErrors.email && t('login.email_error')}
          >
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              value={values.email}
              onChange={(event) => set('email', event.target.value)}
              disabled={submitting}
            />
          </FormField>

          <FormField
            label={t('login.password_label')}
            htmlFor="login-password"
            error={fieldErrors.password && t('login.password_error')}
          >
            <PasswordInput
              id="login-password"
              autoComplete="current-password"
              value={values.password}
              onChange={(event) => set('password', event.target.value)}
              disabled={submitting}
              labels={{
                show_password: t('login.show_password'),
                hide_password: t('login.hide_password'),
              }}
            />
          </FormField>

          {state.kind === 'error' && <AuthFormError error={state.error} />}

          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            className="mt-2 w-full justify-center"
          >
            {submitting ? t('login.submitting') : t('login.submit')}
          </Button>
        </form>

        {/* "New to WagePro? Create a company account" is the EMPLOYER
            company-creation flow (`wageproAuth.signup` mints a company and
            makes the caller its owner). The native shell shows the employee
            surface only (`data/employee_view_only.ts`), so completing it
            there would drop a brand-new company owner onto My Pay with no
            way to set payroll up — company setup is a desktop job. Employees
            never arrive through this link anyway; they come via an emailed
            invite (`invite_accept_screen.tsx`). */}
        {/* An invited employee has no password yet — the invitation email
            carries a one-time code for exactly this step. Shown on web AND in
            the native shell (where tapping the emailed link rarely opens the
            installed app). */}
        <AuthAltActionLink
          href={INVITE_CODE_PATH}
          label={t('login.invite_code_link')}
        />
        {!isNative() && (
          <AuthAltActionLink
            href={SIGNUP_PATH}
            label={t('login.create_account')}
          />
        )}
      </GlassCard>
    </WageProAuthLayout>
  );
}
